import React from 'react';
import { Button, Popconfirm, Table, Tag } from 'antd';
import { DeleteOutlined, FieldTimeOutlined, SaveOutlined } from '@ant-design/icons';
import { SmartBetaView } from '../../../services/smartBetaApi';
import { basketOf, SmartBetaForm, targetWeights } from '../smartBetaForm';
import { Card, CardTitle, Muted, Row } from '../styles';

interface Props {
  saved: SmartBetaView;
  form: SmartBetaForm;
  /** Why it cannot be saved right now, or null. */
  problem: string | null;
  dirty: boolean;
  locked: boolean;
  busy: boolean;
  onSave: () => void;
  onDiscard: () => void;
}

const NextPortfolioCard: React.FC<Props> = ({ saved, form, problem, dirty, locked, busy, onSave, onDiscard }) => {
  // Screens pick at save, so an unsaved edit's picks are not known yet.
  const next = { ...basketOf(form), screened: dirty ? [] : ((saved.next ?? saved.settings).screened ?? []) };
  const current = saved.settings;
  const weight = (list: { symbol: string; weight: number }[], s: string) => list.find((w) => w.symbol === s)?.weight;
  const symbols = [...new Set([...targetWeights(current), ...targetWeights(next)].map((w) => w.symbol))]
    .filter(Boolean)
    .sort();
  const rows = [
    { symbol: 'Cash', now: current.cashWeight, then: next.cashWeight },
    ...symbols.map((s) => ({
      symbol: s,
      now: weight(targetWeights(current), s),
      then: next.blacklist.includes(s) ? 0 : (weight(targetWeights(next), s) ?? 0),
    })),
  ];

  return (
    <Card aria-label="Next portfolio">
      <CardTitle>
        <FieldTimeOutlined /> Next portfolio {saved.next ? <Tag color="gold">Saved</Tag> : <Tag>Not set</Tag>}
      </CardTitle>
      <Row>
        <Muted>
          The rebalance{saved.nextRebalance ? ` on ${saved.nextRebalance}` : ''} trades to this portfolio and it then
          becomes the current one. You can change it until the bot starts; until then the current portfolio stays as it
          is. Without one, the rebalance goes back to the current weights.
        </Muted>
      </Row>
      <Table
        size="small"
        rowKey="symbol"
        pagination={false}
        dataSource={rows}
        columns={[
          { title: 'Ticker', dataIndex: 'symbol' },
          { title: 'Now', align: 'right', render: (_, r) => (r.now == null ? '—' : `${r.now}%`) },
          {
            title: 'Next',
            align: 'right',
            render: (_, r) => {
              const changed = (r.now ?? 0) !== r.then;
              return (
                <span
                  style={{ color: changed ? '#F0B90B' : undefined, fontWeight: changed ? 600 : 400 }}
                >{`${r.then}%`}</span>
              );
            },
          },
        ]}
      />
      {dirty && form.criteria.length > 0 && (
        <Muted>Screened tickers are picked when you save; the table shows them after.</Muted>
      )}
      <Row style={{ marginTop: 12 }}>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={busy}
          disabled={locked || !!problem || !dirty}
          onClick={onSave}
        >
          {saved.next && !dirty ? 'Saved ✓' : 'Save next portfolio'}
        </Button>
        {saved.next && (
          <Popconfirm
            title="Discard the next portfolio?"
            description="The rebalance goes back to the current weights."
            onConfirm={onDiscard}
            okText="Discard"
            disabled={locked}
          >
            <Button icon={<DeleteOutlined />} disabled={locked || busy}>
              Discard
            </Button>
          </Popconfirm>
        )}
      </Row>
      {locked && <Muted>The rebalance has started — the next portfolio opens again once it stops.</Muted>}
      {!locked && problem && dirty && <Muted style={{ color: '#F0B90B' }}>{problem}</Muted>}
    </Card>
  );
};

export default NextPortfolioCard;
